import { useAuthStore } from '@/stores/auth.store';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { useTenantMe } from '@/hooks/tenant/use-tenant-me';
import { Skeleton } from '@/components/ui/skeleton';

export default function DashboardPage() {
  const user = useAuthStore((state) => state.user);
  const { data, isLoading } = useTenantMe();

  const getMetricUsed = (metricName: string): number => {
    if (!data?.usage?.items) return 0;
    const item = data.usage.items.find((i) => i.metric === metricName);
    return item ? item.used : 0;
  };

  const getMetricLimit = (metricName: string): number | null => {
    if (!data?.usage?.items) return null;
    const item = data.usage.items.find((i) => i.metric === metricName);
    return item ? item.limit : null;
  };

  const formatLimit = (limit: number | null) => {
    return limit === null ? 'unlimited' : limit;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-fg-primary">
          Welcome back, {user?.firstName || 'User'}!
        </h1>
        <p className="text-fg-secondary">
          Here is what is happening at {data?.tenant?.name || user?.email || 'your fabricator'} today.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {/* Active Projects Card */}
        <Card className="border border-border/50 bg-bg-elevated/80 shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-fg-secondary">Active Projects</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="text-2xl font-bold text-fg-primary">
                {getMetricUsed('max_projects')}
              </div>
            )}
            <CardDescription className="mt-1 text-xs">
              Limit: {isLoading ? '...' : formatLimit(getMetricLimit('max_projects'))} projects
            </CardDescription>
          </CardContent>
        </Card>

        {/* Offcut Inventory Card */}
        <Card className="border border-border/50 bg-bg-elevated/80 shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-fg-secondary">Offcut Inventory</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="text-2xl font-bold text-fg-primary">
                {getMetricUsed('max_offcut_inventory_items')}
              </div>
            )}
            <CardDescription className="mt-1 text-xs">
              Limit: {isLoading ? '...' : formatLimit(getMetricLimit('max_offcut_inventory_items'))} bars
            </CardDescription>
          </CardContent>
        </Card>

        {/* Optimisation Runs Card */}
        <Card className="border border-border/50 bg-bg-elevated/80 shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-fg-secondary">Optimisation Runs</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="text-2xl font-bold text-fg-primary">
                {getMetricUsed('max_optimisation_runs_per_month')}
              </div>
            )}
            <CardDescription className="mt-1 text-xs">
              Limit: {isLoading ? '...' : formatLimit(getMetricLimit('max_optimisation_runs_per_month'))} runs / mo
            </CardDescription>
          </CardContent>
        </Card>

        {/* Plan Tier Card */}
        <Card className="border border-border/50 bg-bg-elevated/80 shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-fg-secondary">Plan Tier</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-24 bg-primary/20" />
            ) : (
              <div className="text-2xl font-bold text-primary">
                {data?.tenant?.planTierCode || 'FREE'}
              </div>
            )}
            <CardDescription className="mt-1 text-xs">
              Current tenant subscription
            </CardDescription>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
